import { COLOR, COMPANY, SHEET, FONT, SIZE, INR_COMPACT } from '../tokens';
import {
  applyPortraitPrint,
  clipSheetToColumns,
  ensureMinColumnWidths,
  estimateWrappedRowHeight,
  formatGeneratedAt,
  formatPeriod,
  setWidths,
  writeExecutiveHeader,
  writePremiumMetaBand,
  writeSectionGap,
  writeSectionTitle,
  styleTableHeader,
} from '../layout';
import {
  addDataTable,
  applyInrFormatToTableColumn,
  applyTableBodyFont,
  currencyColumn,
  labelTotalColumn,
  sumColumn,
} from '../tableFactory';

const COLS = 7;
const WIDTHS = [30, 10, 18, 18, 18, 16, 20];
const CURRENCY_COLS = [3, 4, 5, 6, 7];

export function buildPaymentChannelsSheet(workbook, ctx, tableName) {
  const { analytics, meta } = ctx;

  const sheet = workbook.addWorksheet(SHEET.CHANNELS, {
    properties: { tabColor: { argb: COLOR.credit }, defaultRowHeight: 20 },
  });
  setWidths(sheet, WIDTHS);
  clipSheetToColumns(sheet, COLS);

  let row = 1;
  row = writeExecutiveHeader(
    sheet,
    row,
    COMPANY.name,
    'Payment Channel Analysis',
    COLS,
  );

  writePremiumMetaBand(sheet, row, COLS,
    [
      { text: `Period: ${formatPeriod(meta.from, meta.to)}`, from: 1, to: 3 },
      { text: `Generated: ${formatGeneratedAt()}`, from: 4, to: COLS },
    ],
    [
      { text: `Report ID: ${meta.reportId || '—'}`, from: 1, to: 3 },
      { text: `${analytics.channels.length} payment channels`, from: 4, to: COLS },
    ],
  );
  row += 2;
  writeSectionGap(sheet, row, COLS);
  row += 1;
  writeSectionTitle(sheet, row, 'Payment Channel Analysis', COLS, { center: true });
  row += 1;

  const tableRows = analytics.channels.map((item) => {
    const net = item.credits - item.debits;
    const volume = item.credits + item.debits;
    return [item.label, item.count, item.credits, item.debits, net, item.gst, volume];
  });

  const table = addDataTable(sheet, {
    startRow: row,
    columns: [
      labelTotalColumn('Channel', { filter: false }),
      sumColumn('Count', { filter: false, total: true }),
      currencyColumn('Credits', { filter: false, total: true }),
      currencyColumn('Debits', { filter: false, total: true }),
      currencyColumn('Net', { filter: false, total: true }),
      currencyColumn('GST', { filter: false, total: true }),
      currencyColumn('Volume', { filter: false, total: true }),
    ],
    rows: tableRows.length ? tableRows : [['No data', 0, 0, 0, 0, 0, 0]],
    totalsRow: tableRows.length > 0,
  });

  CURRENCY_COLS.forEach((ci) => {
    applyInrFormatToTableColumn(
      sheet,
      ci,
      table.dataStartRow,
      table.totalsRow || table.dataEndRow,
      INR_COMPACT,
    );
  });
  applyTableBodyFont(sheet, table.dataStartRow, table.dataEndRow, 1, COLS);
  styleTableHeader(sheet, table.headerRow, [
    { col: 1, align: 'left' },
    { col: 2, align: 'center' },
    { col: 3, align: 'right' },
    { col: 4, align: 'right' },
    { col: 5, align: 'right' },
    { col: 6, align: 'right' },
    { col: 7, align: 'right' },
  ]);

  analytics.channels.forEach((item, i) => {
    const rNum = table.dataStartRow + i;
    const rowHeight = estimateWrappedRowHeight(item.label, WIDTHS[0], { minHeight: 24 });
    sheet.getRow(rNum).height = rowHeight;
    sheet.getCell(rNum, 1).alignment = {
      horizontal: 'left',
      vertical: 'middle',
      wrapText: true,
      indent: 1,
      shrinkToFit: false,
    };
    sheet.getCell(rNum, 2).alignment = { horizontal: 'center', vertical: 'middle' };
    CURRENCY_COLS.forEach((ci) => {
      sheet.getCell(rNum, ci).alignment = { horizontal: 'right', vertical: 'middle', shrinkToFit: false };
    });
    if (item.credits) {
      const c = sheet.getCell(rNum, 3);
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR.creditBg } };
      c.font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.credit } };
    }
    if (item.debits) {
      const c = sheet.getCell(rNum, 4);
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR.debitBg } };
      c.font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.debit } };
    }
    const netCell = sheet.getCell(rNum, 5);
    const net = item.credits - item.debits;
    if (net < 0) {
      netCell.font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.debit } };
    } else if (net > 0) {
      netCell.font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.credit } };
    }
  });

  if (table.totalsRow) {
    sheet.getCell(table.totalsRow, 1).alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: false };
    sheet.getRow(table.totalsRow).height = 28;
  }

  setWidths(sheet, WIDTHS);
  ensureMinColumnWidths(sheet, WIDTHS);
  clipSheetToColumns(sheet, COLS);
  applyPortraitPrint(sheet, {
    colCount: COLS,
    lastRow: table.totalsRow || table.dataEndRow,
    printTitlesRow: '1:4',
    fitOnePage: false,
  });
  ensureMinColumnWidths(sheet, WIDTHS);

  return sheet;
}
